// The search box in the top bar, on every page (module 10 U227; pipeline F3, B1953; SEARCH-AND-FILTER-PLAN-2026-10-04.md
// section 1). / puts the cursor in it from any page without leaving the page; Cmd-K stays the ask bar's (U33).
//
// From two characters, 200 ms after the last key, a panel opens under it with the names that match, by kind, in the
// plan's order, five a kind and the count; then, once the names have answered and the words have stood still, what
// Cobrain read. The arrow keys and Enter open a line, Enter on no line opens /search, Esc closes the panel. At phone
// width the box is an icon in the bar that opens search full screen.
//
// Every answer is the reader's own, read through their rails (R5). A never-said subject (R44) lists nothing and says
// why, in the ask bar's sentence.
//
// At the foot, the reader's own mail and chats Cobrain has not read yet (module 05 R62, F4), asked beside what Cobrain
// read: its count, folded, and one press for the newest five. They open nothing, so the arrow keys pass them by.
import { ActionButton, ActionInput } from '../parts/ActionButton.js';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent as KeyEvent, type ReactNode } from 'react';
import type { Store } from '../api.js';
import {
  askCorpus, askNotYetRead, askRecords, CORPUS_WAIT_MS, failureWords, hitDetail, hitPath, KIND_LABEL, MIN_CHARS, NOT_YET_READ_LABEL,
  NOT_YET_READ_NOTE, PANEL_EACH, passageMeta, passagePath, passageTitle, READ_LABEL, READ_TAB, RECORDS_WAIT_MS, searchPath, unreadPath,
  type Found, type Group, type Refused, type Unread,
} from './search.js';
import './search.css';

type Names = { kind: 'idle' } | { kind: 'loading'; q: string; prev: NamesReady | null } | NamesReady | { kind: 'error'; q: string; message: string };
type NamesReady = { kind: 'ready'; q: string; refused: Refused | null; groups: Group[] };
type Read = { kind: 'idle' } | { kind: 'loading'; q: string } | { kind: 'ready'; q: string; refused: Refused | null; passages: Found[] } | { kind: 'error'; q: string; message: string };
type NotRead = { kind: 'idle' } | { kind: 'ready'; q: string; count: number; passages: Unread[] } | { kind: 'error'; q: string; message: string };

const ICON = <svg className="ico" viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>;

/** Whether a key pressed here was meant as typing: the / shortcut leaves fields, and anything editable, alone. */
export function typingIn(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  return t.isContentEditable || t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT';
}

/** One line the arrow keys can reach, in the order the panel draws them. */
interface Target { key: string; path: string }

export function SearchBox({ stores, go }: { stores: Store[]; go: (path: string) => void }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(false);
  const [names, setNames] = useState<Names>({ kind: 'idle' });
  const [read, setRead] = useState<Read>({ kind: 'idle' });
  const [notRead, setNotRead] = useState<NotRead>({ kind: 'idle' });
  const [unfolded, setUnfolded] = useState(false);
  const [at, setAt] = useState(-1);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const q = text.trim();
  const storeKey = stores.map((s) => s.tenantId).join(',');
  const storesRef = useRef(stores);
  storesRef.current = stores;

  // The names, 200 ms after the last key. Under two characters nothing is asked. An answer for words since changed is
  // let arrive and dropped rather than cancelled: the server reads it either way, and a cancelled request reads as a
  // failed one in the browser (the smoke suite's watch). `ac` is only the flag that says the answer is stale.
  useEffect(() => {
    setUnfolded(false);
    if (q.length < MIN_CHARS) { setNames({ kind: 'idle' }); setRead({ kind: 'idle' }); setNotRead({ kind: 'idle' }); return; }
    const ac = new AbortController();
    setNames((n) => (n.kind === 'ready' && n.q === q ? n : { kind: 'loading', q, prev: n.kind === 'ready' ? n : n.kind === 'loading' ? n.prev : null }));
    const t = window.setTimeout(() => {
      askRecords(storesRef.current, q, PANEL_EACH)
        .then((a) => { if (!ac.signal.aborted) setNames({ kind: 'ready', q, ...a }); })
        .catch((e: unknown) => { if (!ac.signal.aborted) setNames({ kind: 'error', q, message: failureWords(e) }); });
    }, RECORDS_WAIT_MS);
    return () => { window.clearTimeout(t); ac.abort(); };
  }, [q, storeKey]);

  // What Cobrain read, once the names for these words are in and the words have stood still. A refused query is still
  // asked: the corpus route writes the one judgement a refusal leaves (search/corpus.ts) and reads nothing.
  const namesFor = names.kind === 'ready' ? names.q : null;
  useEffect(() => {
    if (namesFor === null || namesFor !== q) return;
    const ac = new AbortController();
    setRead((r) => (r.kind === 'ready' && r.q === q ? r : { kind: 'loading', q }));
    const t = window.setTimeout(() => {
      askCorpus(storesRef.current, q)
        .then((a) => { if (!ac.signal.aborted) setRead({ kind: 'ready', q, ...a }); })
        .catch((e: unknown) => { if (!ac.signal.aborted) setRead({ kind: 'error', q, message: failureWords(e) }); });
    }, CORPUS_WAIT_MS);
    return () => { window.clearTimeout(t); ac.abort(); };
  }, [namesFor, q, storeKey]);

  // The reader's own mail Cobrain has not read yet, asked at the same moment as what Cobrain read, beside it.
  useEffect(() => {
    if (namesFor === null || namesFor !== q) return;
    const ac = new AbortController();
    const t = window.setTimeout(() => {
      askNotYetRead(storesRef.current, q, PANEL_EACH)
        .then((a) => { if (!ac.signal.aborted) setNotRead({ kind: 'ready', q, ...a }); })
        .catch((e: unknown) => { if (!ac.signal.aborted) setNotRead({ kind: 'error', q, message: failureWords(e) }); });
    }, CORPUS_WAIT_MS);
    return () => { window.clearTimeout(t); ac.abort(); };
  }, [namesFor, q, storeKey]);

  // / from any page puts the cursor here, unless the key was typed into a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented || typingIn(e.target)) return;
      e.preventDefault();
      setFull(true);
      setOpen(true);
      input.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // A press anywhere else closes the panel; the words stay, and the panel comes back with them on the next focus.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) { setAt(-1); setOpen(false); setFull(false); } };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const shown: NamesReady | null = names.kind === 'ready' ? names : names.kind === 'loading' ? names.prev : null;
  const refused = shown?.refused ?? (read.kind === 'ready' ? read.refused : null);
  const groups = refused ? [] : (shown?.groups ?? []).filter((g) => g.count > 0);
  const passages = !refused && read.kind === 'ready' && read.q === q ? read.passages.slice(0, PANEL_EACH) : [];

  const unread = !refused && notRead.kind === 'ready' && notRead.q === q && notRead.count > 0 ? notRead : null;
  const nothing = !!shown && !refused && groups.length === 0 && read.kind === 'ready' && read.q === q && passages.length === 0 && !unread;
  const targets: Target[] = [];
  for (const g of groups) for (const h of g.hits) targets.push({ key: `${g.kind}:${h.store.tenantId}:${h.id}`, path: hitPath(h) });
  for (const p of passages) { const path = passagePath(p); if (path) targets.push({ key: `read:${p.store.tenantId}:${p.signalId}`, path }); }
  if (shown && !refused) targets.push({ key: 'everything', path: searchPath(q) });
  useEffect(() => { setAt(-1); }, [shown, read, q]);

  // Opening a line or closing the panel also lets go of the chosen line, so the panel opens again with none chosen.
  const choose = useCallback((path: string) => {
    setAt(-1);
    setOpen(false);
    setFull(false);
    input.current?.blur();
    go(path);
  }, [go]);

  const onKeyDown = (e: KeyEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setAt((i) => Math.min(i + 1, targets.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => Math.max(i - 1, -1)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const t = at >= 0 ? targets[at] : undefined;
      if (t) choose(t.path);
      else if (q.length >= MIN_CHARS) choose(searchPath(q));
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); setAt(-1); setOpen(false); setFull(false); input.current?.blur(); }
  };

  const panel = open && q.length >= MIN_CHARS;
  const active = at >= 0 ? targets[at]?.key : undefined;
  const optionId = (key: string) => `search-opt-${targets.findIndex((t) => t.key === key)}`;
  const line = (key: string, path: string | null, title: string, detail: string | null, personal: boolean, extra?: ReactNode) => (
    <li
      key={key} id={path ? optionId(key) : undefined} role={path ? 'option' : undefined} aria-selected={path ? active === key : undefined}
      className={`search-line${active === key ? ' at' : ''}${personal ? ' private' : ''}${path ? '' : ' still'}`}
      onMouseDown={(e) => e.preventDefault()} onClick={path ? () => choose(path) : undefined}
    >
      <span className="search-t">{title}{personal ? <span className="search-p">Personal</span> : null}</span>
      {detail ? <span className="search-d">{detail}</span> : null}
      {extra}
    </li>
  );

  const readCount = read.kind === 'ready' && read.q === q && !read.refused ? read.passages.length : 0;

  return (
    <div className={`search-box${full ? ' open' : ''}`} ref={box}>
      <ActionButton reading type="button" className="search-icon" aria-label="Search" onClick={() => { setFull(true); setOpen(true); window.setTimeout(() => input.current?.focus(), 0); }}>{ICON}</ActionButton>
      <div className="search-field">
        {ICON}
        <ActionInput reading
          ref={input} type="search" placeholder="Search" aria-label="Search Cobrain" autoComplete="off" spellCheck={false}
          role="combobox" aria-expanded={panel} aria-controls="search-panel" aria-autocomplete="list"
          aria-activedescendant={active ? optionId(active) : undefined}
          value={text} onChange={(e) => { setText(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} onKeyDown={onKeyDown}
        />
        {text !== '' ? <ActionButton reading type="button" className="search-clear" onClick={() => { setText(''); input.current?.focus(); }}>Clear</ActionButton> : <kbd className="search-key" aria-hidden="true">/</kbd>}
        {full ? <ActionButton reading type="button" className="search-close" onClick={() => { setOpen(false); setFull(false); }}>Close</ActionButton> : null}
      </div>
      {panel ? (
        <div className={`search-panel${names.kind === 'loading' && shown ? ' reading' : ''}`} id="search-panel" role="listbox" aria-label={`Search results for ${q}`}>
          {names.kind === 'error' ? <p className="search-note">Search could not be read: {names.message}</p> : null}
          {!shown && names.kind !== 'error' ? <p className="search-note">Searching…</p> : null}
          {refused ? <p className="search-refused">{refused.answer}</p> : null}
          {groups.map((g) => (
            <section key={g.kind} className="search-group" aria-label={KIND_LABEL[g.kind]}>
              <h3>{KIND_LABEL[g.kind]} <span className="search-n">{g.count.toLocaleString('en-GB')}</span></h3>
              <ul>{g.hits.map((h) => line(`${g.kind}:${h.store.tenantId}:${h.id}`, hitPath(h), h.title || 'Untitled', hitDetail(h), h.store.kind === 'personal'))}</ul>
              {g.count > g.hits.length ? <ActionButton reading type="button" className="search-more" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(searchPath(q, g.kind))}>See all {g.count.toLocaleString('en-GB')}</ActionButton> : null}
            </section>
          ))}
          {shown && !refused && !nothing ? (
            <section className="search-group" aria-label={READ_LABEL}>
              <h3>{READ_LABEL}{readCount > 0 ? <> <span className="search-n">{readCount}</span></> : null}</h3>
              {read.kind === 'loading' || (read.kind === 'ready' && read.q !== q) || read.kind === 'idle' ? <p className="search-note">Reading…</p> : null}
              {read.kind === 'error' ? <p className="search-note">What Cobrain read could not be searched: {read.message}</p> : null}
              {read.kind === 'ready' && read.q === q && passages.length === 0 ? <p className="search-note">Nothing Cobrain read mentions it.</p> : null}
              {passages.length > 0 ? (
                <ul>{passages.map((p) => line(`read:${p.store.tenantId}:${p.signalId}`, passagePath(p), passageTitle(p), passageMeta(p), p.store.kind === 'personal', <span className="search-q">{p.passage}</span>))}</ul>
              ) : null}
              {readCount > passages.length ? <ActionButton reading type="button" className="search-more" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(searchPath(q, READ_TAB))}>See all {readCount}</ActionButton> : null}
            </section>
          ) : null}
          {unread ? (
            <section className="search-group search-unread" aria-label={NOT_YET_READ_LABEL}>
              <h3>{NOT_YET_READ_LABEL} <span className="search-n">{unread.count.toLocaleString('en-GB')}</span></h3>
              <ActionButton reading type="button" className="search-more" aria-expanded={unfolded} onMouseDown={(e) => e.preventDefault()} onClick={() => setUnfolded((u) => !u)}>
                {unfolded ? 'Hide them' : 'Show them'}
              </ActionButton>
              {unfolded ? (
                <>
                  <ul>{unread.passages.map((p) => line(`unread:${p.store.tenantId}:${p.signalId}`, null, passageTitle(p), passageMeta(p), p.store.kind === 'personal', <span className="search-q">{p.passage}</span>))}</ul>
                  <p className="search-note">{NOT_YET_READ_NOTE}</p>
                  {unread.count > unread.passages.length ? <ActionButton reading type="button" className="search-more" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(unreadPath(q))}>See all {unread.count.toLocaleString('en-GB')}</ActionButton> : null}
                </>
              ) : null}
            </section>
          ) : null}
          {!refused && notRead.kind === 'error' && notRead.q === q ? <p className="search-note">Your mail Cobrain has not read could not be searched: {notRead.message}</p> : null}
          {nothing ? <p className="search-note">Nothing found for “{q}”.</p> : null}
          {!refused && shown ? (
            <ActionButton reading
              type="button" id={optionId('everything')} className={`search-all${active === 'everything' ? ' at' : ''}`}
              onMouseDown={(e) => e.preventDefault()} onClick={() => choose(searchPath(q))}
            >See everything for “{q}”</ActionButton>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
